import { Building2 } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { MemberCard as MemberCardData } from "@/features/members/members.types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cardHover, stagger } from "@/lib/cards";

/**
 * A steward, as the directory shows them — and as the admin queue shows an
 * applicant, with the decision underneath (`footer`). The whole card opens
 * the profile except the legacy buttons, which open each legacy: the name's
 * link stretches an overlay across the card, and the buttons lift above it.
 */
export function MemberCard({
  member,
  index = 0,
  markNew = false,
  footer,
}: {
  member: MemberCardData;
  index?: number;
  /** Review mode, for the admin queue: badge a pending legacy as New — it is
   *  approved with the application, so the reviewer can see what one decision
   *  covers. */
  markNew?: boolean;
  footer?: ReactNode;
}) {
  const legacies = member.organizations ?? [];
  return (
    <Card
      // Full height only in review mode, so the decision sits on the same
      // line across a row however much the applicants differ — one has three
      // legacies and a location, the next has a name and nothing else, and
      // buttons that wander up and down the row are ones you have to aim at.
      // The directory keeps its cards their natural height.
      className={`group animate-item-enter relative ${footer ? "h-full" : ""} ${cardHover}`}
      style={stagger(index)}
    >
      <CardContent className="flex flex-1 gap-3">
        <Avatar className="size-12">
          <AvatarImage src={member.image_url ?? undefined} alt={member.name} />
          <AvatarFallback>{member.name.charAt(0) || "\u00b7"}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col">
          {/* The name was never sent, so stand a bar where it would be —
              honest about the withholding rather than an empty line.

              The bar carried an aria-label and nothing a sighted reader could
              see, so the page showed a grey rectangle and left them to infer
              why. Todd, after the 1 September demo: "on any obscured data,
              make it clear what is hidden here, plus a hover/tooltip state to
              call to action to apply/join."

              It says so, and it opens the profile like any other card: the
              bar refused to navigate until 2026-09-24, so clicking a card in
              the preview did nothing while its URL worked perfectly well
              (June). It goes to the profile, never to /apply — a grey bar
              routing to the application would be a way in that is not blue,
              which `applyCta.test.ts` refuses, and a row of blue bars where
              names belong would read as an error. */}
          {member.redacted ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`/directory/${member.id}`}
                  aria-label="Name hidden. Names are shown to members."
                  className="bg-muted-foreground/25 hover:bg-muted-foreground/40 mt-1 mb-1.5 block h-3 w-28 rounded-sm after:absolute after:inset-0"
                />
              </TooltipTrigger>
              <TooltipContent>
                Names are shown to members. Apply for membership below.
              </TooltipContent>
            </Tooltip>
          ) : (
            <Link
              to={`/directory/${member.id}`}
              className="truncate font-medium underline-offset-4 after:absolute after:inset-0 group-hover:underline"
            >
              {member.name}
            </Link>
          )}
          {member.location && (
            <p className="text-muted-foreground truncate text-xs">{member.location}</p>
          )}
          {legacies.length > 0 && (
            <div className="relative z-10 mt-2 flex flex-wrap items-start gap-x-1.5 gap-y-2">
              {legacies.map((o) => {
                // Filed with this application, and approved along with it — so
                // the reviewer can see what the one decision covers. A caption
                // under the button rather than a badge inside it: the badge sat
                // between the name and the button's edge and made both harder
                // to read, on the row where reading the name is the job.
                const isNew = markNew && o.status === "pending";
                return (
                  <span key={o.id} className="flex flex-col items-start gap-0.5">
                    {/* Wraps rather than running off the card: a Button is
                        nowrap at a fixed height, and organization names like
                        "Akseli Gallen-Kallela catalogue raisonné project" are
                        longer than the card is wide. */}
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className={cn(
                        "h-auto min-h-7 max-w-full items-start py-1 text-left whitespace-normal [&_svg]:mt-0.5",
                        isNew && "border-brand"
                      )}
                    >
                      <Link to={`/organizations/${o.id}`}>
                        <Building2 data-slot="icon" />
                        {o.name}
                      </Link>
                    </Button>
                    {isNew && (
                      // brand-ink, not the fill: this is small text on white,
                      // where the fill blue is 1.6:1.
                      <span className="text-brand-ink pl-1 text-[0.65rem] font-medium tracking-wide uppercase">
                        New
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          )}
          {(member.mediums ?? []).length > 0 && (
            <div className="mt-auto flex flex-wrap gap-1 pt-2">
              {(member.mediums ?? []).slice(0, 3).map((k) => (
                <Badge key={k} variant="secondary" className="text-xs">
                  {k}
                </Badge>
              ))}
            </div>
          )}
          {footer && <div className="relative z-10 mt-auto pt-3">{footer}</div>}
        </div>
      </CardContent>
    </Card>
  );
}
